import { supabase } from '../lib/supabase';

/**
 * Submission Service
 * Handles paper submission operations with submission file storage
 * Converts between snake_case (database) and camelCase (React)
 */

export const submissionService = {
  formatConferenceParticipantId(conferenceShortName, participantNumber) {
    if (!participantNumber) return null;

    const prefix = (conferenceShortName || 'CONF')
      ?.toString()
      ?.toUpperCase()
      ?.replace(/[^A-Z0-9]/g, '')
      ?.slice(0, 10) || 'CONF';

    return `${prefix}-${String(participantNumber)?.padStart(4, '0')}`;
  },

  /**
   * Upload the main submission file to storage.
   */
  async uploadSubmissionFile(file, userId, conferenceId = null) {
    try {
      const fileExt = (file?.name?.split('.')?.pop() || 'pdf').toLowerCase();

      let paperNumber = null;
      let shortName = 'submission';
      if (conferenceId) {
        // Query next paper number and conference acronym in parallel
        const [{ data: latest }, { data: conf }] = await Promise.all([
          supabase
            ?.from('conference_submissions')
            ?.select('paper_number')
            ?.eq('conference_id', conferenceId)
            ?.order('paper_number', { ascending: false })
            ?.limit(1)
            ?.maybeSingle(),
          supabase
            ?.from('conferences')
            ?.select('short_name')
            ?.eq('id', conferenceId)
            ?.maybeSingle()
        ]);
        paperNumber = (latest?.paper_number || 0) + 1;
        shortName = conf?.short_name || 'submission';
      }

      const fileName = `${paperNumber}-${shortName}.${fileExt}`;
      const filePath = conferenceId
        ? `${userId}/${conferenceId}/${fileName}`
        : `${userId}/${fileName}`;

      const { data, error } = await supabase?.storage?.from('conference-submissions')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      return {
        path: data?.path,
        fileName: file?.name,
        fileSize: file?.size
      };
    } catch (error) {
      console.error('Upload submission file error:', error);
      throw error;
    }
  },

  /**
   * Backward-compatible alias for older callers.
   */
  async uploadPDF(file, userId) {
    return this.uploadSubmissionFile(file, userId);
  },

  /**
   * Get a signed URL for a submission file with expiration.
   * Includes access logging and verification for reviewers
   */
  async getSubmissionFileUrl(filePath, expirationSeconds = 3600, reviewerId = null, downloadFilename = null) {
    try {
      if (!filePath) {
        throw new Error('File path is required');
      }

      // Generate signed URL (valid for 1 hour by default).
      // Passing `download` forces Content-Disposition: attachment so the browser
      // always downloads the file with the correct name instead of trying to render it.
      const signOptions = downloadFilename ? { download: downloadFilename } : { download: true };
      const { data, error } = await supabase
        ?.storage
        ?.from('conference-submissions')
        ?.createSignedUrl(filePath, expirationSeconds, signOptions);

      if (error) throw error;

      // TODO: Log access for audit trail
      // if (reviewerId) {
      //   await this.logSubmissionFileAccess(filePath, reviewerId);
      // }

      return {
        url: data?.signedUrl,
        expiresIn: expirationSeconds,
        expiresAt: new Date(Date.now() + expirationSeconds * 1000).toISOString()
      };
    } catch (error) {
      console.error('Get submission file URL error:', error);
      throw error;
    }
  },

  /**
   * Get signed URLs for multiple submission files at once.
   */
  async getSubmissionFileUrls(filePaths, expirationSeconds = 86400) {
    try {
      const uniquePaths = [...new Set((filePaths || [])?.filter(Boolean))];
      if (uniquePaths?.length === 0) {
        return new Map();
      }

      const { data, error } = await supabase
        ?.storage
        ?.from('conference-submissions')
        ?.createSignedUrls(uniquePaths, expirationSeconds);

      if (error) throw error;

      return new Map(
        uniquePaths?.map((path, index) => [path, data?.[index]?.signedUrl || null])
      );
    } catch (error) {
      console.error('Get submission file URLs error:', error);
      throw error;
    }
  },

  /**
   * Backward-compatible alias for older callers.
   */
  async getPDFUrl(filePath, expirationSeconds = 3600, reviewerId = null) {
    return this.getSubmissionFileUrl(filePath, expirationSeconds, reviewerId);
  },

  /**
   * Verify reviewer has access to a submission.
   * Checks if reviewer is assigned to this submission for review.
   */
  async verifyReviewerAccess(reviewerId, submissionId) {
    try {
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.select('id')
        ?.eq('submission_id', submissionId)
        ?.eq('reviewer_id', reviewerId)
        ?.maybeSingle();

      if (error) throw error;

      if (!data) {
        return {
          hasAccess: false,
          reason: 'Not assigned as reviewer for this submission'
        };
      }

      return {
        hasAccess: true,
        reviewId: data?.id
      };
    } catch (error) {
      console.error('Verify reviewer access error:', error);
      throw error;
    }
  },

  /**
   * Get a submission file with access verification.
   * Safe method for reviewers to access papers.
   */
  async getSubmissionFileForReviewer(submissionId, reviewerId) {
    try {
      // Verify access first
      const accessCheck = await this.verifyReviewerAccess(reviewerId, submissionId);
      if (!accessCheck?.hasAccess) {
        throw new Error(accessCheck?.reason);
      }

      // Get submission to get file path
      const { data: submission, error: subError } = await supabase
        ?.from('conference_submissions')
        ?.select('pdf_url')
        ?.eq('id', submissionId)
        ?.single();

      if (subError) throw subError;
      if (!submission?.pdf_url) {
        throw new Error('Submission file not found for this submission');
      }

      // Get signed URL
      const urlData = await this.getSubmissionFileUrl(submission?.pdf_url, 3600, reviewerId);

      return {
        url: urlData?.url,
        expiresAt: urlData?.expiresAt,
        submissionId,
        reviewId: accessCheck?.reviewId
      };
    } catch (error) {
      console.error('Get submission file for reviewer error:', error);
      throw error;
    }
  },

  /**
   * Backward-compatible alias for older callers.
   */
  async getPDFForReviewer(submissionId, reviewerId) {
    return this.getSubmissionFileForReviewer(submissionId, reviewerId);
  },

  /**
   * Delete a submission file from storage.
   */
  async deleteSubmissionFile(filePath) {
    try {
      const { error } = await supabase?.storage?.from('conference-submissions')?.remove([filePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Delete submission file error:', error);
      throw error;
    }
  },

  /**
   * Backward-compatible alias for older callers.
   */
  async deletePDF(filePath) {
    return this.deleteSubmissionFile(filePath);
  },

  /**
   * Get or create a stable per-conference participant number for the user.
   */
  async getOrCreateConferenceParticipantNumber(conferenceId, userId) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_or_create_conference_participant_number', {
          p_conference_id: conferenceId,
          p_user_id: userId
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get conference participant number error:', error);
      throw error;
    }
  },

  async getConferenceParticipantMap(userId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_participants')
        ?.select(`
          conference_id,
          participant_number,
          conferences (
            short_name,
            name
          )
        `)
        ?.eq('user_id', userId);

      if (error) throw error;

      return new Map(
        (data || [])?.map((participant) => [
          participant?.conference_id,
          {
            participantNumber: participant?.participant_number,
            participantId: this.formatConferenceParticipantId(
              participant?.conferences?.short_name || participant?.conferences?.name,
              participant?.participant_number
            )
          }
        ])
      );
    } catch (error) {
      console.error('Get conference participant map error:', error);
      throw error;
    }
  },

  /**
   * Create submission
   */
  async create(submissionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('conference_submissions')?.insert({
          conference_id: submissionData?.conferenceId,
          user_id: user?.id,
          track_id: submissionData?.trackId || null,
          title: submissionData?.title,
          abstract: submissionData?.abstract,
          keywords: submissionData?.keywords,
          pdf_url: submissionData?.pdfUrl,
          pdf_file_name: submissionData?.pdfFileName,
          pdf_file_size: submissionData?.pdfFileSize,
          supplementary_files: submissionData?.supplementaryFiles || [],
          co_authors: submissionData?.coAuthors || [],
          status: submissionData?.status || 'draft',
          submitted_at: submissionData?.status === 'submitted' ? new Date()?.toISOString() : null
        })?.select()?.single();

      if (error) throw error;
      return { id: data?.id };
    } catch (error) {
      console.error('Create submission error:', error);
      throw error;
    }
  },

  /**
   * Update submission
   */
  async update(submissionId, updates) {
    try {
      const updateData = {
        title: updates?.title,
        abstract: updates?.abstract,
        keywords: updates?.keywords,
        track_id: updates?.trackId || null,
        co_authors: updates?.coAuthors,
        supplementary_files: updates?.supplementaryFiles
      };

      if (updates?.pdfUrl) {
        updateData.pdf_url = updates?.pdfUrl;
        updateData.pdf_file_name = updates?.pdfFileName;
        updateData.pdf_file_size = updates?.pdfFileSize;
      }

      if (updates?.status === 'submitted' && !updates?.submittedAt) {
        updateData.status = 'submitted';
        updateData.submitted_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('conference_submissions')?.update(updateData)?.eq('id', submissionId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update submission error:', error);
      throw error;
    }
  },

  /**
   * Get user submissions for a conference
   */
  async getUserSubmissions(conferenceId, userId) {
    try {
      const participantMap = await this.getConferenceParticipantMap(userId);
      const { data, error } = await supabase?.from('conference_submissions')?.select(`
          *,
          conference_tracks (name)
        `)?.eq('conference_id', conferenceId)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      const conferenceParticipant = participantMap?.get(conferenceId);

      return data?.map(sub => ({
        id: sub?.id,
        conferenceId: sub?.conference_id,
        userId: sub?.user_id,
        trackId: sub?.track_id,
        trackName: sub?.conference_tracks?.name,
        title: sub?.title,
        abstract: sub?.abstract,
        keywords: sub?.keywords,
        paperNumber: sub?.paper_number,
        pdfUrl: sub?.pdf_url,
        pdfFileName: sub?.pdf_file_name,
        pdfFileSize: sub?.pdf_file_size,
        supplementaryFiles: sub?.supplementary_files,
        coAuthors: sub?.co_authors,
        status: sub?.status,
        submittedAt: sub?.submitted_at,
        updatedAt: sub?.updated_at,
        createdAt: sub?.created_at,
        cameraReadyUrl: sub?.camera_ready_url,
        cameraReadyFileName: sub?.camera_ready_file_name,
        cameraReadyFileSize: sub?.camera_ready_file_size,
        cameraReadySubmittedAt: sub?.camera_ready_submitted_at,
        participantNumber: conferenceParticipant?.participantNumber || null,
        participantId: conferenceParticipant?.participantId || null
      })) || [];
    } catch (error) {
      console.error('Get user submissions error:', error);
      throw error;
    }
  },

  /**
   * Get submission by ID
   */
  async getById(submissionId) {
    try {
      const { data, error } = await supabase?.from('conference_submissions')?.select(`
          *,
          conference_tracks (name),
          user_profiles (first_name, last_name, email, institution),
          conferences (name, short_name)
        `)?.eq('id', submissionId)?.maybeSingle();

      if (error) throw error;
      if (!data) throw Object.assign(new Error('Submission not found or access denied'), { code: 'PGRST116' });

      return {
        id: data?.id,
        conferenceId: data?.conference_id,
        userId: data?.user_id,
        trackId: data?.track_id,
        trackName: data?.conference_tracks?.name,
        title: data?.title,
        abstract: data?.abstract,
        keywords: data?.keywords,
        pdfUrl: data?.pdf_url,
        pdfFileName: data?.pdf_file_name,
        pdfFileSize: data?.pdf_file_size,
        paperNumber: data?.paper_number,
        conferenceName: data?.conferences?.name,
        conferenceShortName: data?.conferences?.short_name,
        supplementaryFiles: data?.supplementary_files,
        coAuthors: data?.co_authors,
        status: data?.status,
        submittedAt: data?.submitted_at,
        updatedAt: data?.updated_at,
        createdAt: data?.created_at,
        cameraReadyUrl: data?.camera_ready_url,
        cameraReadyFileName: data?.camera_ready_file_name,
        cameraReadyFileSize: data?.camera_ready_file_size,
        cameraReadySubmittedAt: data?.camera_ready_submitted_at,
        author: {
          firstName: data?.user_profiles?.first_name,
          lastName: data?.user_profiles?.last_name,
          email: data?.user_profiles?.email,
          institution: data?.user_profiles?.institution
        }
      };
    } catch (error) {
      console.error('Get submission by ID error:', error);
      throw error;
    }
  },

  /**
   * Get all user submissions across all conferences
   */
  async getAllUserSubmissions(userId) {
    try {
      const participantMap = await this.getConferenceParticipantMap(userId);
      const { data, error } = await supabase?.from('conference_submissions')?.select(`
          *,
          conferences (
            id,
            name,
            short_name
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(submission => ({
        id: submission?.id,
        conferenceId: submission?.conference_id,
        trackId: submission?.track_id,
        title: submission?.title,
        abstract: submission?.abstract,
        keywords: submission?.keywords,
        pdfUrl: submission?.pdf_url,
        pdfFileName: submission?.pdf_file_name,
        pdfFileSize: submission?.pdf_file_size,
        status: submission?.status,
        coAuthors: submission?.co_authors,
        createdAt: submission?.created_at,
        updatedAt: submission?.updated_at,
        conferenceName: submission?.conferences?.name,
        conferenceShortName: submission?.conferences?.short_name,
        participantNumber: participantMap?.get(submission?.conference_id)?.participantNumber || null,
        participantId: participantMap?.get(submission?.conference_id)?.participantId || null
      }));
    } catch (error) {
      console.error('Error fetching all user submissions:', error);
      throw error;
    }
  },

  /**
   * Get all user submissions with comprehensive review and decision status
   * Includes: review count, average scores, decisions, and final acceptance status
   */
  async getAllUserSubmissionsWithReviewStatus(userId) {
    try {
      const participantMap = await this.getConferenceParticipantMap(userId);
      const { data, error } = await supabase?.from('conference_submissions')?.select(`
          *,
          conferences (
            id,
            name,
            short_name,
            decision_date,
            paper_deadline,
            camera_ready_deadline
          ),
          conference_tracks (
            name
          ),
          submission_reviews (
            id,
            overall_score,
            decision,
            is_draft,
            submitted_at,
            confidence_level,
            detailed_feedback,
            strengths,
            weaknesses
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(submission => {
        const reviews = submission?.submission_reviews || [];
        const completedReviews = reviews?.filter(r => !r?.is_draft && r?.submitted_at);
        const inProgressReviews = reviews?.filter(r => r?.is_draft);

        // Calculate review statistics
        const totalReviews = reviews?.length;
        const completedCount = completedReviews?.length;
        const avgScore = completedCount > 0
          ? completedReviews?.reduce((sum, r) => sum + (r?.overall_score || 0), 0) / completedCount
          : null;

        // Determine decision summary
        const decisions = completedReviews?.map(r => r?.decision)?.filter(d => d);
        const decisionCounts = {
          accept: decisions?.filter(d => d === 'accept')?.length || 0,
          minor_revision: decisions?.filter(d => d === 'minor_revision')?.length || 0,
          major_revision: decisions?.filter(d => d === 'major_revision')?.length || 0,
          reject: decisions?.filter(d => d === 'reject')?.length || 0
        };

        // Determine overall review status
        let reviewStatus = 'pending';
        if (submission?.status === 'draft') {
          reviewStatus = 'draft';
        } else if (totalReviews === 0) {
          reviewStatus = 'awaiting_assignment';
        } else if (inProgressReviews?.length > 0 && completedCount === 0) {
          reviewStatus = 'in_progress';
        } else if (completedCount > 0 && completedCount < totalReviews) {
          reviewStatus = 'partially_reviewed';
        } else if (completedCount === totalReviews && totalReviews > 0) {
          reviewStatus = 'completed';
        }

        // Check if feedback is available (at least one completed review)
        const hasFeedback = completedCount > 0;

        return {
          id: submission?.id,
          conferenceId: submission?.conference_id,
          trackId: submission?.track_id,
          trackName: submission?.conference_tracks?.name,
          title: submission?.title,
          abstract: submission?.abstract,
          keywords: submission?.keywords,
          pdfUrl: submission?.pdf_url,
          pdfFileName: submission?.pdf_file_name,
          pdfFileSize: submission?.pdf_file_size,
          status: submission?.status,
          coAuthors: submission?.co_authors,
          submittedAt: submission?.submitted_at,
          createdAt: submission?.created_at,
          updatedAt: submission?.updated_at,
          cameraReadyUrl: submission?.camera_ready_url,
          cameraReadyFileName: submission?.camera_ready_file_name,
          cameraReadyFileSize: submission?.camera_ready_file_size,
          cameraReadySubmittedAt: submission?.camera_ready_submitted_at,
          // Accepted-with-conditions: the author needs to see what was asked.
          permissionFormUrl: submission?.permission_form_url || null,
          permissionFormFileName: submission?.permission_form_file_name || null,
          permissionFormSubmittedAt: submission?.permission_form_submitted_at || null,
          revisionSeverity: submission?.revision_severity || null,
          revisionNote: submission?.revision_note || null,
          revisionRequestedAt: submission?.revision_requested_at || null,
          participantNumber: participantMap?.get(submission?.conference_id)?.participantNumber || null,
          participantId: participantMap?.get(submission?.conference_id)?.participantId || null,
          conference: {
            id: submission?.conferences?.id,
            name: submission?.conferences?.name,
            shortName: submission?.conferences?.short_name,
            decisionDate: submission?.conferences?.decision_date,
            paperDeadline: submission?.conferences?.paper_deadline,
            cameraReadyDeadline: submission?.conferences?.camera_ready_deadline
          },
          reviewInfo: {
            totalReviews,
            completedReviews: completedCount,
            inProgressReviews: inProgressReviews?.length,
            averageScore: avgScore,
            reviewStatus,
            hasFeedback,
            decisions: decisionCounts
          }
        };
      });
    } catch (error) {
      console.error('Error fetching user submissions with review status:', error);
      throw error;
    }
  },

  /**
   * Get submissions by author (user ID)
   * Returns submissions with conference details for dashboard
   */
  async getByAuthor(userId) {
    try {
      const participantMap = await this.getConferenceParticipantMap(userId);
      const { data, error } = await supabase?.from('conference_submissions')?.select(`
          *,
          conferences (
            id,
            name,
            short_name,
            status,
            start_date,
            end_date
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(submission => ({
        id: submission?.id,
        conferenceId: submission?.conference_id,
        trackId: submission?.track_id,
        title: submission?.title,
        abstract: submission?.abstract,
        keywords: submission?.keywords,
        pdfUrl: submission?.pdf_url,
        pdfFileName: submission?.pdf_file_name,
        pdfFileSize: submission?.pdf_file_size,
        status: submission?.status,
        coAuthors: submission?.co_authors,
        createdAt: submission?.created_at,
        updatedAt: submission?.updated_at,
        submittedAt: submission?.submitted_at,
        conference: submission?.conferences,
        participantNumber: participantMap?.get(submission?.conference_id)?.participantNumber || null,
        participantId: participantMap?.get(submission?.conference_id)?.participantId || null
      })) || [];
    } catch (error) {
      console.error('Error fetching submissions by author:', error);
      return [];
    }
  },

  /**
   * Get submission statistics for user
   */
  async getUserStats(userId) {
    try {
      const { data, error } = await supabase?.from('conference_submissions')?.select('status')?.eq('user_id', userId);

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        submitted: data?.filter(s => s?.status === 'submitted')?.length || 0,
        draft: data?.filter(s => s?.status === 'draft')?.length || 0,
        underReview: data?.filter(s => s?.status === 'under_review')?.length || 0,
        accepted: data?.filter(s => s?.status === 'accepted')?.length || 0,
        rejected: data?.filter(s => s?.status === 'rejected')?.length || 0
      };

      return stats;
    } catch (error) {
      console.error('Get user stats error:', error);
      throw error;
    }
  },

  /**
   * Upload camera-ready file
   */
  async uploadCameraReady(submissionId, file, userId) {
    try {
      const fileExt = (file?.name?.split('.')?.pop() || 'pdf').toLowerCase();

      // Fetch paper_number, conference_id and acronym — submission already exists.
      // Also grab the existing camera-ready path so we can replace the old file.
      const { data: sub } = await supabase
        ?.from('conference_submissions')
        ?.select('paper_number, conference_id, camera_ready_url, conferences(short_name)')
        ?.eq('id', submissionId)
        ?.maybeSingle();

      const paperNumber = sub?.paper_number || 'unknown';
      const shortName = sub?.conferences?.short_name || 'submission';
      const fileName = `${paperNumber}-${shortName}.${fileExt}`;
      const filePath = sub?.conference_id
        ? `${userId}/${sub.conference_id}/camera-ready/${fileName}`
        : `${userId}/camera-ready/${fileName}`;
      const previousPath = sub?.camera_ready_url;

      // Overwrite any existing file at this path (re-uploads / replacements).
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('conference-submissions')
        ?.upload(filePath, file, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      // Update submission record (camera_ready_submitted_at = latest upload time)
      const { data, error } = await supabase?.from('conference_submissions')
        ?.update({
          camera_ready_url: uploadData?.path,
          camera_ready_file_name: file?.name,
          camera_ready_file_size: file?.size,
          camera_ready_submitted_at: new Date()?.toISOString()
        })
        ?.eq('id', submissionId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Clean up the old file if the new upload landed at a different path
      // (e.g. the author switched between .pdf and .docx).
      if (previousPath && previousPath !== uploadData?.path) {
        const { error: removeError } = await supabase?.storage
          ?.from('conference-submissions')
          ?.remove([previousPath]);
        if (removeError) console.warn('Failed to remove old camera-ready file:', removeError?.message);
      }

      return {
        path: uploadData?.path,
        fileName: file?.name,
        fileSize: file?.size,
        submittedAt: data?.camera_ready_submitted_at
      };
    } catch (error) {
      console.error('Upload camera-ready error:', error);
      throw error;
    }
  },

  /**
   * Upload the signed permission request form.
   *
   * Deliberately parallel to uploadCameraReady: same bucket, same per-author
   * folder, same replace-and-clean-up behaviour. The two files travel together
   * through the whole flow, so anything that behaves differently between them
   * becomes a thing to remember, and things people have to remember get got
   * wrong.
   */
  async uploadPermissionForm(submissionId, file, userId) {
    try {
      const fileExt = (file?.name?.split('.')?.pop() || 'pdf').toLowerCase();

      const { data: sub } = await supabase
        ?.from('conference_submissions')
        ?.select('paper_number, conference_id, permission_form_url, conferences(short_name)')
        ?.eq('id', submissionId)
        ?.maybeSingle();

      const paperNumber = sub?.paper_number || 'unknown';
      const shortName = sub?.conferences?.short_name || 'submission';
      const fileName = `${paperNumber}-${shortName}-permission.${fileExt}`;
      const filePath = sub?.conference_id
        ? `${userId}/${sub.conference_id}/permission-form/${fileName}`
        : `${userId}/permission-form/${fileName}`;
      const previousPath = sub?.permission_form_url;

      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('conference-submissions')
        ?.upload(filePath, file, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      const { data, error } = await supabase?.from('conference_submissions')
        ?.update({
          permission_form_url: uploadData?.path,
          permission_form_file_name: file?.name,
          permission_form_file_size: file?.size,
          permission_form_submitted_at: new Date()?.toISOString()
        })
        ?.eq('id', submissionId)
        ?.select()
        ?.single();

      if (error) throw error;

      if (previousPath && previousPath !== uploadData?.path) {
        const { error: removeError } = await supabase?.storage
          ?.from('conference-submissions')
          ?.remove([previousPath]);
        if (removeError) console.warn('Failed to remove old permission form:', removeError?.message);
      }

      return {
        path: uploadData?.path,
        fileName: file?.name,
        fileSize: file?.size,
        submittedAt: data?.permission_form_submitted_at
      };
    } catch (error) {
      console.error('Upload permission form error:', error);
      throw error;
    }
  },

  /**
   * Get camera-ready status for accepted submissions in a conference
   */
  async getCameraReadyStatus(conferenceId) {
    try {
      const { data, error } = await supabase?.from('conference_submissions')
        ?.select(`
          id, title, status, camera_ready_url, camera_ready_file_name,
          camera_ready_file_size, camera_ready_submitted_at,
          user_profiles!conference_submissions_user_id_fkey (first_name, last_name, email)
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('status', 'accepted')
        ?.order('title', { ascending: true });

      if (error) throw error;

      return data?.map(sub => ({
        id: sub?.id,
        title: sub?.title,
        authorName: sub?.user_profiles
          ? `${sub?.user_profiles?.first_name} ${sub?.user_profiles?.last_name}`
          : 'Unknown',
        authorEmail: sub?.user_profiles?.email || '',
        cameraReadyUrl: sub?.camera_ready_url,
        cameraReadyFileName: sub?.camera_ready_file_name,
        cameraReadyFileSize: sub?.camera_ready_file_size,
        cameraReadySubmittedAt: sub?.camera_ready_submitted_at
      })) || [];
    } catch (error) {
      console.error('Get camera-ready status error:', error);
      throw error;
    }
  },

  /**
   * Delete submission
   */
  async delete(submissionId) {
    try {
      const submission = await this.getById(submissionId);
      
      if (submission?.pdfUrl) {
        await this.deleteSubmissionFile(submission?.pdfUrl);
      }

      const { error } = await supabase?.from('conference_submissions')?.delete()?.eq('id', submissionId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete submission error:', error);
      throw error;
    }
  }
};

export default submissionService;
